import React, { useEffect, useRef, useState } from "react";

const TARGET = 1233;
const ENTRANCE_FROM = TARGET - 54;

function usePrefersReducedMotion() {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        const update = () => setReduced(mq.matches);
        update();
        mq.addEventListener?.("change", update);
        return () => mq.removeEventListener?.("change", update);
    }, []);
    return reduced;
}

export const LiveCounter = ({ label, sub }) => {
    const reduced = usePrefersReducedMotion();
    const [display, setDisplay] = useState(reduced ? TARGET : ENTRANCE_FROM);

    const sectionRef = useRef(null);
    const targetRef = useRef(TARGET);
    const displayRef = useRef(reduced ? TARGET : ENTRANCE_FROM);
    const rafRef = useRef(null);
    const timeoutRef = useRef(null);

    const tweenTo = (to, duration = 800) => {
        cancelAnimationFrame(rafRef.current);
        const from = displayRef.current;
        const diff = to - from;
        if (Math.abs(diff) < 0.001) { displayRef.current = to; setDisplay(to); return; }
        const startedAt = performance.now();
        const ease = (p) => 1 - Math.pow(1 - p, 3);
        const step = (now) => {
            const p = Math.min(1, (now - startedAt) / duration);
            const val = from + diff * ease(p);
            displayRef.current = val;
            setDisplay(val);
            if (p < 1) rafRef.current = requestAnimationFrame(step);
            else { displayRef.current = to; setDisplay(to); }
        };
        rafRef.current = requestAnimationFrame(step);
    };

    const scheduleNext = () => {
        const delay = 1000 + Math.random() * 2000; // 1–3s
        timeoutRef.current = setTimeout(() => {
            const r = Math.random();
            const inc = r < 0.5 ? 1 : r < 0.8 ? 2 : 3 + Math.floor(Math.random() * 2);
            targetRef.current += inc;
            tweenTo(targetRef.current, 650);
            scheduleNext();
        }, delay);
    };

    useEffect(() => {
        if (reduced) {
            displayRef.current = TARGET;
            setDisplay(TARGET);
            targetRef.current = TARGET;
            return;
        }
        const el = sectionRef.current;
        if (!el) return;
        const io = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    if (displayRef.current < TARGET && targetRef.current === TARGET) {
                        tweenTo(TARGET, 1100);
                    }
                    clearTimeout(timeoutRef.current);
                    scheduleNext();
                } else {
                    clearTimeout(timeoutRef.current);
                    cancelAnimationFrame(rafRef.current);
                }
            },
            { threshold: 0.35 }
        );
        io.observe(el);
        return () => {
            io.disconnect();
            clearTimeout(timeoutRef.current);
            cancelAnimationFrame(rafRef.current);
        };
    }, [reduced]);

    const shown = Math.round(display).toLocaleString("en-US");

    return (
        <section ref={sectionRef} className="relative px-4 sm:px-6 py-20 sm:py-28 text-center overflow-hidden">
            <div
                className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] max-w-[120%] h-[640px] rounded-full blur-3xl opacity-70"
                style={{ background: "radial-gradient(circle, hsl(var(--accent) / 0.16), transparent 62%)" }}
            />
            <div className="relative max-w-3xl mx-auto">
                <div className="inline-flex items-center gap-2 text-[12.5px] font-medium tracking-wide text-muted-foreground">
                    <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500 shadow-[0_0_10px_2px_rgba(16,185,129,0.4)]" />
                    Live
                </div>
                <div
                    data-testid="live-counter-number"
                    className="mt-4 font-heading font-semibold tracking-tight tabular-nums leading-none bg-gradient-to-b from-foreground to-foreground/55 bg-clip-text text-transparent"
                    style={{ fontSize: "clamp(3.5rem, 14vw, 9rem)" }}
                    aria-live="polite"
                >
                    {shown}
                </div>
                <div className="mt-4 text-lg sm:text-2xl font-semibold" data-testid="live-counter-label">{label}</div>
                <p className="mt-2 text-sm sm:text-base text-muted-foreground">{sub}</p>
            </div>
        </section>
    );
};

export default LiveCounter;
